import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, FileText, Filter, Globe, Search } from 'lucide-react';
import { PROJECT_ID } from '../config/Config';
import { fetchPublishedLibraryItems } from '../services/PublicResearchRepository';
import { ScholarMetadata } from '../components/seo/ScholarMetadata';
import { LegalDisclaimer } from '../components/LegalDisclaimer';
import { formatCitationText } from '../utils/formatCitationText';

const TYPE_LABELS = {
  legal_case: 'Jurisprudência',
  legislation: 'Legislação',
  book: 'Doutrina / Livro',
  'article-journal': 'Artigo Científico',
  thesis: 'Tese Acadêmica',
};

export default function PublicResearch() {
  const appId = PROJECT_ID || 'portalrda-f312d';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [selectedDoc, setSelectedDoc] = useState(null);

  const getAuthorshipLabel = (type) => {
    const t = String(type || '').toLowerCase();
    if (t === 'legal_case') return 'TRIBUNAL / RELATOR';
    if (t === 'legislation') return 'JURISDIÇÃO';
    return 'AUTOR(A)';
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const visible = await fetchPublishedLibraryItems(appId);
        setItems(visible);
      } catch (e) {
        console.error('[PublicResearch] load error:', e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = String(term || '').trim().toLowerCase();
    return (items || []).filter((i) => {
      const matchesTerm =
        !q ||
        `${String(i?.title || '')} ${String(i?.author || '')} ${String(i?.year || '')} ${String(i?.type || '')}`
          .toLowerCase()
          .includes(q);
      const matchesType = filterType === 'ALL' || String(i?.type || '') === filterType;
      return matchesTerm && matchesType;
    });
  }, [filterType, items, term]);

  if (selectedDoc) {
    const summaryText =
      selectedDoc?.summary ||
      selectedDoc?.resumo ||
      selectedDoc?.ementa ||
      selectedDoc?.abstract ||
      selectedDoc?.description ||
      '';

    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-12 animate-fade-in max-w-full overflow-x-hidden">
        <ScholarMetadata doc={selectedDoc} />

        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => setSelectedDoc(null)}
            className="flex items-center gap-2 text-gray-500 hover:text-[#C5A059] mb-6 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar para a Biblioteca
          </button>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-300 max-w-full overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                {TYPE_LABELS[selectedDoc.type] || selectedDoc.type || 'Documento'}
              </span>
              {selectedDoc.year ? <span className="text-[10px] text-gray-400">• {selectedDoc.year}</span> : null}
            </div>

            <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2 break-words whitespace-normal max-w-full">
              {String(selectedDoc.title || '')}
            </h1>

            <div className="text-sm font-medium text-gray-600 mb-6 break-words whitespace-normal max-w-full">
              {selectedDoc.author ? String(selectedDoc.author) : 'Não informado'}
            </div>

            {String(summaryText || '').trim() ? (
              <div className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap max-w-full overflow-hidden mb-8">
                {String(summaryText)}
              </div>
            ) : null}

            <div className="flex flex-col gap-3">
              {String(selectedDoc.display_html || '').trim() ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm font-serif text-gray-800 text-justify break-words max-w-full overflow-hidden">
                  <div
                    className="break-words whitespace-normal max-w-full overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: formatCitationText(String(selectedDoc.display_html || '')) }}
                  />
                </div>
              ) : null}

              {selectedDoc.mirror_url ? (
                <a
                  href={String(selectedDoc.mirror_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#20243B] text-white py-3 rounded-lg font-bold hover:bg-[#1a1e30] transition-transform active:scale-95 inline-flex items-center justify-center gap-2"
                >
                  <FileText className="w-5 h-5" />
                  Baixar Cópia do Acervo (PDF)
                </a>
              ) : selectedDoc.url ? (
                <a
                  href={String(selectedDoc.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-transform active:scale-95 inline-flex items-center justify-center gap-2"
                >
                  <Globe className="w-5 h-5" />
                  Acessar Link Externo
                </a>
              ) : (
                <div className="text-center p-4 text-gray-400 italic bg-gray-50 rounded-lg w-full">Acesso indisponível</div>
              )}

              {selectedDoc.url ? (
                <div className="text-[10px] text-gray-400 break-words whitespace-normal max-w-full">
                  Fonte: {String(selectedDoc.url)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12 animate-fade-in max-w-full overflow-x-hidden">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10 text-center md:text-left border-b border-gray-200 pb-6">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#20243B] flex items-center gap-3 justify-center md:justify-start">
            <BookOpen className="w-8 h-8 text-[#C5A059]" />
            Biblioteca Digital
          </h1>
          <p className="text-sm text-gray-500 mt-2 ml-0 md:ml-11">
            Acervo jurídico especializado em Direito Animal.
          </p>

          <div className="mt-4">
            <LegalDisclaimer />
          </div>
        </header>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-8 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-[#C5A059] focus:bg-white transition-colors"
              placeholder="Pesquisar por título, autor ou tema..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="relative md:w-64">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-[#C5A059] appearance-none cursor-pointer"
            >
              <option value="ALL">Todos os Tipos</option>
              <option value="legal_case">Jurisprudência</option>
              <option value="legislation">Legislação</option>
              <option value="book">Doutrina</option>
              <option value="article-journal">Artigos</option>
              <option value="thesis">Teses</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#C5A059] rounded-full mx-auto mb-2"></div>
            Carregando acervo...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
            <p className="text-gray-500">Nenhum documento encontrado.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedDoc(item)}
                className="bg-white p-6 rounded-xl border border-gray-300 shadow-sm hover:border-[#C5A059] transition-all group relative cursor-pointer"
              >
                <div className="flex flex-col md:flex-row justify-between gap-4 items-stretch">
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                        {TYPE_LABELS[item.type] || item.type || 'Documento'}
                      </span>
                      {item.year ? <span className="text-[10px] text-gray-400">• {item.year}</span> : null}
                    </div>

                    <h3
                      className="font-serif font-bold text-[#20243B] text-lg leading-snug mb-2 whitespace-normal max-w-full break-normal line-clamp-2"
                    >
                      {String(item.title || '')}
                    </h3>

                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#C5A059] rounded-full"></span>
                      {item.author || 'Autor não informado'}
                    </p>
                  </div>

                  <div className="flex items-start md:items-center justify-end">
                    {item.mirror_url ? (
                      <div className="bg-gray-50 text-gray-400 p-3 rounded-full group-hover:bg-[#20243B] group-hover:text-white transition-colors shadow-sm shrink-0" title="PDF no Acervo">
                        <FileText className="w-5 h-5" />
                      </div>
                    ) : item.url ? (
                      <div className="bg-gray-50 text-gray-400 p-3 rounded-full group-hover:bg-blue-600 group-hover:text-white transition-colors shadow-sm shrink-0" title="Link Externo">
                        <Globe className="w-5 h-5" />
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic px-4">Acesso indisponível</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}